import CanvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import { shuffle } from "slotworks/utils/collection-utils";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import DisplayObject = PIXI.DisplayObject;
import Sequence from "slotworks/utils/contracts/sequence";
import Point from "slotworks/utils/geom/point";
import { Tween } from "slotworks/utils/tween";

class ScreenShakeComponent {

    public shakeKeyframes = [
        { x: 1, y: 1 },
        { x: -1, y: -2 },
        { x: -3, y: 0 },
        { x: 3, y: 2 },
        { x: 1, y: -1 },
        { x: -1, y: 2 }
    ];

    /*
        All layers to use this class MUST have a bounds defined
     */
    public layersToShake = [
        Layers.Banners,
        Layers.SymbolAnimations,
        Layers.MatrixBackground,
        Layers.MatrixForeground,
        Layers.MatrixContent,
        Layers.MatrixForeground
    ];

    public shakesPerSecond = 1;
    public shakeDuration: number = 750;
    public shakeDomBackground: boolean = false;

    private toTween: { x: number, y: number } = { x: 0, y: 0 };
    private shakes: Shake[] = [];
    private canvasTween: Tween;
    private canvasContract: Contract<void>;
    private domTween: Tween;

    public init(extraLayers?: Layers[]) {
        if (extraLayers) {
            this.layersToShake.push(...extraLayers);
        }

        CanvasManager.onOrientationChange.add((orientation: Orientation) => this.updateOrientation(orientation), null);
    }

    public shakeScreen(duration: number = this.shakeDuration): Contract<void> {
        const keyframes = this.getShakeKeyFrames();
        return new Sequence([
            () => Contract.wrap(() => {
                this.stopShakeScreen();
            }),
            () => new Parallel([
                () => this.shakeCanvasLayers(duration, keyframes),
                () => this.shakeBackground(duration, keyframes)
            ])
        ]);
    }

    public stopShakeScreen(): void {

        this.resetDomLayers();
        this.resetCanvas();

        if (this.canvasContract) {
            this.canvasContract.forceResolve();
            this.canvasContract = null;
        }
    }

    protected shakeCanvasLayers(duration: number, keyframes: Array<{ x: number, y: number }>): Contract<void> {

        const x = keyframes.map((data) => data.x);
        const y = keyframes.map((data) => data.y);

        this.layersToShake.forEach((layer: Layers) => {
            this.shakes.push(new Shake(layer.container));
        });

        this.canvasTween = new Tween(this.toTween);
        this.canvasTween.to({ x, y }, duration / 4)
            .repeat(4)
            .onStart(() => {
                this.shakes.forEach((shake: Shake) => {
                    shake.start();
                });
            })
            .onUpdate(() => {
                this.shakes.forEach((shake: Shake) => {
                    shake.position(this.toTween.x, this.toTween.y);
                });
            })
            .onComplete(() => {
                this.resetCanvas();
            });

        this.canvasContract = new Parallel([
            () => Contract.getTimeoutContract(duration),
            () => Contract.wrap(() => {
                this.canvasTween.start();
            })
        ]);

        return this.canvasContract;
    }

    protected shakeBackground(duration: number, keyframes: Array<{ x: number, y: number }>): Contract<void> {
        if (!this.shakeDomBackground) {
            return Contract.empty();
        }

        const x = keyframes.map((data) => (data.x));
        const y = keyframes.map((data) => (data.y));

        const sceneElements = this.getDOMBackgroundScenes();
        const translateValues = { x: 0, y: 0 };

        this.domTween = new Tween(translateValues)
            .to({ x, y }, duration / 4)
            .repeat(4)
            .onUpdate(() => {
                sceneElements.forEach((element) => {
                    element.style.top = translateValues.y + "px";
                    element.style.left = translateValues.x + "px";
                });
            })
            .onComplete(() => {
                this.resetDomLayers();
            })
            .start();

        return Contract.empty();
    }

    protected getDOMBackgroundScenes(): HTMLDivElement[] {
        const scenes: HTMLDivElement[] = [];
        Layers.Background.getScenes().forEach((scene) => {
            const element = document.querySelector(`#Background #${scene.name}`) as HTMLDivElement;
            scenes.push(element);
        });
        return scenes;
    }

    private resetCanvas() {
        if (this.canvasTween) {
            this.canvasTween.stop();
            this.canvasTween = null;
        }

        this.shakes.forEach((shake: Shake) => {
            shake.stop();
        });

        this.shakes = [];
    }

    private resetDomLayers() {
        if (this.domTween) {
            this.domTween.stop();
            this.domTween = null;
        }

        const sceneElements = this.getDOMBackgroundScenes();

        sceneElements.forEach((element) => {
            element.style.removeProperty("top");
            element.style.removeProperty("left");
        });
    }

    private getShakeKeyFrames() {
        let shakesToPerform = Math.max(1, (this.shakeDuration / 1000) / this.shakesPerSecond);
        const shakeKeyframes = [];

        while (shakesToPerform > 0) {
            shakesToPerform--;
            shakeKeyframes.push(...shuffle([...this.shakeKeyframes]));
        }

        return shakeKeyframes;
    }

    private updateOrientation(orientation: Orientation) {

        this.shakes.forEach((shake: Shake) => {
            shake.resetOriginalPosition();
            shake.position(this.toTween.x, this.toTween.y);
        });
    }
}

export default new ScreenShakeComponent();

export class Shake {
    private container: DisplayObject;
    private originalPosition: Point;

    constructor(container: DisplayObject) {
        this.container = container;
        this.resetOriginalPosition();
    }

    public start() {
        //
    }

    public stop() {
        this.resetPosition();
    }

    public resetOriginalPosition() {
        this.originalPosition = new Point(this.container.x, this.container.y);
    }

    public position(x: number, y: number) {
        this.container.position.set(this.originalPosition.x + x, this.originalPosition.y + y);
    }

    private resetPosition() {
        this.container.position.set(this.originalPosition.x, this.originalPosition.y);
    }
}
